<template>
  <div class="purchase-management">
    <div class="page-header">
      <h1>{{ $t('stall.purchase') }}</h1>
      <p class="subtitle">{{ $t("purchase.subtitle") }}</p>
    </div>
    
    <!-- 搜索和筛选 -->
    <div class="search-section">
      <div class="search-form">
        <div class="form-group">
          <label>{{ $t("purchase.dateRange") }}</label>
          <input v-model="filters.startDate" type="date" class="form-control" />
        </div>
        <div class="form-group">
          <label>{{ $t("purchase.to") }}</label>
          <input v-model="filters.endDate" type="date" class="form-control" />
        </div>
        <div class="form-group">
          <label>{{ $t('navigation.products') }}</label>
          <select v-model="filters.productId" class="form-control">
            <option value="">{{ $t('common.all') }}{{ $t('navigation.products') }}</option>
            <option v-for="product in products" :key="product.id" :value="product.id">
              {{ product.name }}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label>{{ $t("purchase.supplier") }}</label>
          <select v-model="filters.supplierId" class="form-control">
            <option value="">{{ $t('common.all') }}供应商</option>
            <option v-for="supplier in suppliers" :key="supplier.id" :value="supplier.id">
              {{ supplier.name }}
            </option>
          </select>
        </div>
        <button class="btn btn-primary" @click="searchPurchases">
          <i class="icon-search"></i> {{ $t("purchase.search") }}
        </button>
        <button class="btn btn-secondary" @click="resetFilters">{{ $t('common.reset') }}</button>
      </div>
      <div class="actions">
        <button class="btn btn-primary" @click="showNewPurchaseModal">
          <i class="icon-plus"></i> {{ $t("purchase.newPurchase") }}
        </button>
        <button class="btn btn-secondary" @click="exportPurchases">
          <i class="icon-download"></i> {{ $t("purchase.export") }}
        </button>
      </div>
    </div>
    
    <!-- 采购统计 -->
    <div class="statistics-bar">
      <div class="stat-item">
        <span class="label">{{ $t("purchase.totalAmount") }}：</span>
        <span class="value">¥{{ totalPurchaseAmount }}</span>
      </div>
      <div class="stat-item">
        <span class="label">{{ $t("purchase.totalCount") }}：</span>
        <span class="value">{{ totalPurchaseCount }} {{ $t("purchase.records") }}</span>
      </div>
      <div class="stat-item">
        <span class="label">{{ $t("purchase.avgPrice") }}：</span>
        <span class="value">¥{{ averagePrice }}</span>
      </div>
      <div class="stat-item">
        <span class="label">总{{ $t('navigation.products') }}数：</span>
        <span class="value">{{ totalItems }} {{ $t("purchase.items") }}</span>
      </div>
    </div>
    
    <!-- 采购列表 -->
    <div class="table-container">
      <table class="purchase-table">
        <thead>
          <tr>
            <th width="60">{{ $t("purchase.seq") }}</th>
            <th width="120">{{ $t("purchase.date") }}</th>
            <th width="100">{{ $t('navigation.products') }}名称</th>
            <th width="80">{{ $t('navigation.products') }}编码</th>
            <th width="80">采购{{ $t('common.quantity') }}</th>
            <th width="80">{{ $t("purchase.unitPrice") }}</th>
            <th width="100">采购{{ $t('common.amount') }}</th>
            <th width="100">{{ $t("purchase.supplier") }}</th>
            <th width="80">{{ $t('common.status') }}</th>
            <th width="150">{{ $t('common.action') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="purchaseList.length === 0">
            <td colspan="10" class="empty-cell">
              <div class="empty-state">
                <i class="icon-inbox"></i>
                <p>{{ $t("purchase.noRecords") }}</p>
              </div>
            </td>
          </tr>
          <tr v-for="(purchase, index) in purchaseList" :key="purchase.id">
            <td>{{ (currentPage - 1) * pageSize + index + 1 }}</td>
            <td>{{ formatDate(purchase.createdAt) }}</td>
            <td>
              <div class="product-cell">
                <div class="product-name">{{ purchase.productName }}</div>
              </div>
            </td>
            <td>{{ purchase.productCode }}</td>
            <td>{{ purchase.quantity }}</td>
            <td>¥{{ purchase.unitPrice }}</td>
            <td class="amount">¥{{ purchase.totalAmount }}</td>
            <td>{{ purchase.supplierName }}</td>
            <td>
              <span :class="['status', `status-${purchase.status}`]">
                {{ statusLabel[purchase.status] }}
              </span>
            </td>
            <td>
              <button class="btn-action" @click="viewPurchaseDetail(purchase.id)">
                <i class="icon-eye"></i> {{ $t("purchase.view") }}
              </button>
              <button class="btn-action" @click="editPurchase(purchase.id)">
                <i class="icon-edit"></i> {{ $t('common.edit') }}
              </button>
              <button class="btn-action danger" @click="deletePurchase(purchase.id)">
                <i class="icon-trash"></i> {{ $t('common.delete') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      
      <!-- 分页 -->
      <div class="pagination-container">
        <div class="pagination-info">
          {{ totalCount }} {{ $t("pagination.records") }}，
          <select v-model.number="pageSize" @change="searchPurchases" class="page-size-select">
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
        <div class="pagination-controls">
          <button :disabled="currentPage === 1" @click="currentPage--" class="btn-page">
            <i class="icon-chevron-left"></i> {{ $t("pagination.prev") }}
          </button>
          <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
          <button :disabled="currentPage === totalPages" @click="currentPage++" class="btn-page">
            {{ $t("pagination.next") }} <i class="icon-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
    
    <!-- 新增采购单模态框 -->
    <div v-if="showPurchaseModal" class="modal-overlay" @click="closePurchaseModal">
      <div class="modal-dialog" @click.stop>
        <div class="modal-header">
          <h2>{{ purchaseFormTitle }}</h2>
          <button class="btn-close" @click="closePurchaseModal">×</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitPurchase">
            <div class="form-group">
              <label>{{ $t('navigation.products') }} <span class="required">*</span></label>
              <select v-model="purchaseForm.productId" class="form-control" required>
                <option value="">{{ $t('common.select') }}{{ $t('navigation.products') }}</option>
                <option v-for="product in products" :key="product.id" :value="product.id">
                  {{ product.name }} - 当前库存: {{ product.stock }}
                </option>
              </select>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label>采购{{ $t('common.quantity') }} <span class="required">*</span></label>
                <input v-model.number="purchaseForm.quantity" type="number" min="1" class="form-control" required :placeholder="$t('Stall.Purchase.Index.line176_placeholder')" />
              </div>
              <div class="form-group">
                <label>{{ $t('Stall.Purchase.Index.line179_text') }} <span class="required">*</span></label>
                <input v-model.number="purchaseForm.unitPrice" type="number" step="0.0001" class="form-control" required :placeholder="$t('Stall.Purchase.Index.line180_placeholder')" />
              </div>
            </div>
            
            <div class="form-group">
              <label>{{ $t('Stall.Purchase.Index.line185_text') }} <span class="required">*</span></label>
              <select v-model="purchaseForm.supplierId" class="form-control" required>
                <option value="">{{ $t('common.select') }}供应商</option>
                <option v-for="supplier in suppliers" :key="supplier.id" :value="supplier.id">
                  {{ supplier.name }}
                </option>
              </select>
            </div>
            
            <div class="form-group">
              <label>{{ $t('Stall.Purchase.Index.line195_text') }}</label>
              <input v-model="purchaseForm.deliveryDate" type="date" class="form-control" />
            </div>
            
            <div class="form-group">
              <label>{{ $t('common.remark') }}</label>
              <textarea v-model="purchaseForm.remarks" class="form-control" rows="3" :placeholder="$t('Stall.Purchase.Index.line201_placeholder')"></textarea>
            </div>
            
            <div class="form-group total">
              <label>采购{{ $t('common.amount') }}：</label>
              <span class="total-amount">¥{{ calculateTotal() }}</span>
            </div>
            
            <div class="form-actions">
              <button type="button" class="btn btn-secondary" @click="closePurchaseModal">
                {{ $t('common.cancel') }}
              </button>
              <button type="submit" class="btn btn-primary">
                {{ purchaseForm.id ? '更新' : '创建' }}采购单
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'PurchaseManagement',
  data() {
    return {
      filters: {
        startDate: this.getDateString(-30),
        endDate: this.getDateString(0),
        productId: '',
        supplierId: ''
      },
      purchaseList: [],
      products: [],
      suppliers: [],
      showPurchaseModal: false,
      purchaseForm: {
        id: null,
        productId: '',
        quantity: 1,
        unitPrice: 0,
        supplierId: '',
        deliveryDate: '',
        remarks: ''
      },
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      totalPages: 0,
      statusLabel: {
        pending: '待收货',
        received: '已收货',
        cancelled: '已取消'
      },
      totalPurchaseAmount: '0.00',
      totalPurchaseCount: 0,
      averagePrice: '0.00',
      totalItems: 0
    };
  },
  computed: {
    purchaseFormTitle() {
      return this.purchaseForm.id ? '编辑采购单' : '新增采购单';
    }
  },
  methods: {
    ...mapActions('stall', [
      'getPurchases',
      'getProducts',
      'getSuppliers',
      'createPurchase',
      'updatePurchase',
      'deletePurchase',
      'getPurchaseDetail'
    ]),
    
    async loadPurchases() {
      try {
        const response = await this.getPurchases({
          startDate: this.filters.startDate,
          endDate: this.filters.endDate,
          productId: this.filters.productId,
          supplierId: this.filters.supplierId,
          page: this.currentPage,
          pageSize: this.pageSize
        });
        this.purchaseList = response.data;
        this.totalCount = response.totalCount;
        this.totalPages = Math.ceil(this.totalCount / this.pageSize);
        this.calculateStatistics();
      } catch (error) {
        this.$message.error(this.$t('purchase.loadFailed'));
      }
    },
    
    async loadProducts() {
      try {
        this.products = await this.getProducts();
      } catch (error) {
        console.error('加载商品失败', error);
      }
    },
    
    async loadSuppliers() {
      try {
        this.suppliers = await this.getSuppliers();
      } catch (error) {
        console.error('加载供应商列表失败', error);
      }
    },
    
    calculateStatistics() {
      if (this.purchaseList.length === 0) {
        this.totalPurchaseAmount = '0.00';
        this.totalPurchaseCount = 0;
        this.averagePrice = '0.00';
        this.totalItems = 0;
        return;
      }
      
      const total = this.purchaseList.reduce((sum, purchase) => sum + parseFloat(purchase.totalAmount), 0);
      const count = this.purchaseList.length;
      const items = this.purchaseList.reduce((sum, purchase) => sum + purchase.quantity, 0);
      
      this.totalPurchaseAmount = total.toFixed(2);
      this.totalPurchaseCount = count;
      this.averagePrice = (total / count).toFixed(2);
      this.totalItems = items;
    },
    
    searchPurchases() {
      this.currentPage = 1;
      this.loadPurchases();
    },
    
    resetFilters() {
      this.filters = {
        startDate: this.getDateString(-30),
        endDate: this.getDateString(0),
        productId: '',
        supplierId: ''
      };
      this.currentPage = 1;
      this.loadPurchases();
    },
    
    showNewPurchaseModal() {
      this.purchaseForm = {
        id: null,
        productId: '',
        quantity: 1,
        unitPrice: 0,
        supplierId: '',
        deliveryDate: '',
        remarks: ''
      };
      this.showPurchaseModal = true;
    },
    
    closePurchaseModal() {
      this.showPurchaseModal = false;
    },
    
    async editPurchase(purchaseId) {
      try {
        const purchase = await this.getPurchaseDetail(purchaseId);
        this.purchaseForm = {
          id: purchase.id,
          productId: purchase.productId,
          quantity: purchase.quantity,
          unitPrice: purchase.unitPrice,
          supplierId: purchase.supplierId,
          deliveryDate: purchase.deliveryDate,
          remarks: purchase.remarks
        };
        this.showPurchaseModal = true;
      } catch (error) {
        this.$message.error('加载采购单详情失败');
      }
    },
    
    async submitPurchase() {
      try {
        if (this.purchaseForm.id) {
          await this.updatePurchase({ id: this.purchaseForm.id, ...this.purchaseForm });
          this.$message.success('采购单已更新');
        } else {
          await this.createPurchase(this.purchaseForm);
          this.$message.success('采购单已创建');
        }
        this.closePurchaseModal();
        this.loadPurchases();
      } catch (error) {
        this.$message.error('操作失败');
      }
    },
    
    async deletePurchase(purchaseId) {
      if (!confirm('确定要删除这条采购记录吗？')) {
        return;
      }
      
      try {
        await this.deletePurchase(purchaseId);
        this.$message.success('采购记录已删除');
        this.loadPurchases();
      } catch (error) {
        this.$message.error('删除失败');
      }
    },
    
    viewPurchaseDetail(purchaseId) {
      this.$router.push(`/stall/purchase/${purchaseId}`);
    },
    
    calculateTotal() {
      return (this.purchaseForm.quantity * this.purchaseForm.unitPrice).toFixed(2);
    },
    
    exportPurchases() {
      this.$message.success(this.$t('purchase.exporting'));
    },
    
    formatDate(dateString) {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    },
    
    getDateString(days) {
      const date = new Date();
      date.setDate(date.getDate() + days);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  },
  
  mounted() {
    this.loadProducts();
    this.loadSuppliers();
    this.loadPurchases();
  }
};
</script>

<style scoped lang="scss">
.purchase-management {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
  
  .page-header {
    margin-bottom: 20px;
    h1 {
      font-size: 24px;
      font-weight: bold;
      color: #2c3e50;
      margin: 0 0 8px 0;
    }
    .subtitle {
      color: #999;
      font-size: 13px;
      margin: 0;
    }
  }
  
  // 搜索和筛选
  .search-section {
    background: white;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    
    .search-form {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 12px;
      margin-bottom: 16px;
      
      .form-group {
        display: flex;
        flex-direction: column;
        label {
          font-size: 12px;
          font-weight: 500;
          color: #666;
          margin-bottom: 4px;
        }
        .form-control {
          padding: 8px 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 12px;
          font-family: inherit;
          &:focus {
            outline: none;
            border-color: #3498db;
            box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.1);
          }
        }
      }
      button {
        align-self: flex-end;
      }
    }
    
    .actions {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      button {
        min-width: 120px;
      }
    }
  }
  
  // 统计栏
  .statistics-bar {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 16px;
    margin-bottom: 20px;
    
    .stat-item {
      background: white;
      padding: 16px;
      border-radius: 8px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      
      .label {
        font-size: 12px;
        color: #999;
        margin-right: 8px;
      }
      .value {
        font-size: 16px;
        font-weight: bold;
        color: #3498db;
      }
    }
  }
  
  // 表格容器
  .table-container {
    background: white;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    
    .purchase-table {
      width: 100%;
      border-collapse: collapse;
      
      thead {
        background-color: #f5f7fa;
        border-bottom: 2px solid #ddd;
        th {
          padding: 12px;
          text-align: left;
          font-size: 12px;
          font-weight: 600;
          color: #666;
        }
      }
      
      tbody {
        tr {
          border-bottom: 1px solid #eee;
          transition: background-color 0.3s ease;
          
          &:hover {
            background-color: #f9f9f9;
          }
          
          td {
            padding: 12px;
            font-size: 12px;
            color: #666;
            
            &.empty-cell {
              padding: 0;
            }
            
            &.amount {
              color: #3498db;
              font-weight: 600;
            }
            
            .status {
              display: inline-block;
              padding: 4px 8px;
              border-radius: 3px;
              font-size: 11px;
              font-weight: 500;
              
              &.status-pending {
                background-color: #fff3cd;
                color: #856404;
              }
              &.status-received {
                background-color: #d4edda;
                color: #155724;
              }
              &.status-cancelled {
                background-color: #f8d7da;
                color: #721c24;
              }
            }
            
            .product-cell {
              .product-name {
                font-weight: 500;
                color: #2c3e50;
              }
            }
          }
        }
      }
    }
    
    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 60px 20px;
      i {
        font-size: 48px;
        color: #ddd;
        margin-bottom: 16px;
      }
      p {
        color: #999;
        font-size: 14px;
        margin: 0;
      }
    }
  }
  
  // 表格操作按钮
  .btn-action {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 8px;
    margin-right: 4px;
    background: none;
    border: none;
    color: #3498db;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      color: #2980b9;
    }
    
    &.danger {
      color: #e74c3c;
      &:hover {
        color: #c0392b;
      }
    }
    
    i {
      font-size: 13px;
    }
  }
  
  // 分页
  .pagination-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-top: 1px solid #eee;
    background: white;
    
    .pagination-info {
      font-size: 12px;
      color: #666;
      .page-size-select {
        padding: 4px 8px;
        border: 1px solid #ddd;
        border-radius: 4px;
        margin: 0 6px;
        font-size: 12px;
        background: white;
      }
    }
    
    .pagination-controls {
      display: flex;
      align-items: center;
      gap: 12px;
      
      .btn-page {
        padding: 6px 12px;
        border: 1px solid #ddd;
        background: white;
        border-radius: 4px;
        font-size: 12px;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        gap: 4px;
        
        &:hover:not(:disabled) {
          border-color: #3498db;
          color: #3498db;
        }
        
        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
      
      .page-info {
        font-size: 12px;
        color: #666;
        min-width: 60px;
        text-align: center;
      }
    }
  }
  
  // 模态框
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    
    .modal-dialog {
      background: white;
      border-radius: 8px;
      width: 90%;
      max-width: 600px;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
      
      .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px;
        border-bottom: 1px solid #eee;
        
        h2 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #2c3e50;
        }
        
        .btn-close {
          background: none;
          border: none;
          font-size: 24px;
          color: #999;
          cursor: pointer;
          &:hover {
            color: #2c3e50;
          }
        }
      }
      
      .modal-body {
        padding: 20px;
        
        form {
          display: flex;
          flex-direction: column;
          gap: 16px;
          
          .form-group {
            display: flex;
            flex-direction: column;
            
            label {
              font-size: 12px;
              font-weight: 500;
              color: #666;
              margin-bottom: 6px;
              
              .required {
                color: #e74c3c;
              }
            }
            
            .form-control {
              padding: 10px 12px;
              border: 1px solid #ddd;
              border-radius: 4px;
              font-size: 12px;
              font-family: inherit;
              &:focus {
                outline: none;
                border-color: #3498db;
                box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.1);
              }
            }
            
            &.total {
              flex-direction: row;
              justify-content: space-between;
              align-items: center;
              padding: 12px;
              background-color: #f5f7fa;
              border-radius: 4px;
              
              label {
                margin: 0;
              }
              
              .total-amount {
                font-size: 18px;
                font-weight: bold;
                color: #3498db;
              }
            }
          }
          
          .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
          }
          
          .form-actions {
            display: flex;
            gap: 12px;
            justify-content: flex-end;
            margin-top: 20px;
            
            button {
              min-width: 100px;
            }
          }
        }
      }
    }
  }
  
  // 通用按钮样式
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &.btn-primary {
      background-color: #3498db;
      color: white;
      &:hover {
        background-color: #2980b9;
      }
    }
    
    &.btn-secondary {
      background-color: #ecf0f5;
      color: #666;
      &:hover {
        background-color: #d5dce3;
      }
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}
</style>